
import { CheckupRequestInfo, ValidatedInstructionPageDefinition } from '@companion-professional/dctypes';
import React from 'react';
import BasePage from '../../base/BasePage';
import { VideoPlayer } from '@companion-professional/components';
import { NeedHelpContactSupport } from '../../../../components/NeedHelpContactSupport';
import {Markdown} from "@companion-professional/components/Markdown";
import { NativeCameraCaptureButtons } from '../../../../components/CaptureWithVideoInstructions/NativeCameraCaptureButtons';
import { MobileCameraHelpLink } from '../../../../components/CaptureWithVideoInstructions/MobileCameraHelpLink';
import { trackEvent } from '../../../../lib/trackEvent';
import "@mux/mux-player";

interface StdInstructionsPageProps {
  defn: ValidatedInstructionPageDefinition;
  checkupRequest: CheckupRequestInfo;
  page: number;
}

const renderPopupContent = (popupContent: string | undefined) => {
  if (popupContent && popupContent !== '') {
    return <Markdown>{popupContent}</Markdown>;
  }
  return undefined;
}

/*
Remove empty functions in buttons components: InBrowserCaptureButtons and NativeCameraCaptureButtons -
once the old logic is removed.
*/
export const StdInstructionsPage = ({ defn, checkupRequest, page }: StdInstructionsPageProps) => {
  const pageKind = 'std_instructions';
  
  const renderInstructionVideo = (defn: ValidatedInstructionPageDefinition) => {
    const instructionVideoPoster = `https://image.mux.com/${defn.video_playback_id}/thumbnail.png?width=480&time=${defn.video_poster_seconds}`;
    return (
        <VideoPlayer
            muxPlaybackId={defn.video_playback_id}
            poster={instructionVideoPoster}
            muted={false}
            className="object-contain w-full h-full"
            emitVideoWatchedMetrics={
              (properties) => {                
                trackEvent('instruction_video_watch_metrics', {
                  ...properties,
                  page: page, 
                  pageKind: pageKind,
                });
            }}
        />
    )
  }

  const renderInstructionImage = (defn: ValidatedInstructionPageDefinition) => {
    return (
        <img 
          src={defn.img_url} 
          alt="Instruction"
          className="object-contain w-full h-full"
        />
    )
  }

  const renderEitherVideoOrImage = (defn: ValidatedInstructionPageDefinition) => {
    if (defn.video_playback_id) {
      return renderInstructionVideo(defn);
    }
    return renderInstructionImage(defn);
  }

  return (
    <BasePage
      page={page}
      pageKind={pageKind}
      checkupRequest={checkupRequest}
      renderChild={() => (
    
      <div className="flex flex-col h-dvh w-full overflow-auto">
        <div className="flex flex-col h-full w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto">

          {/* 
            Media container: max-height 40vh, content scales to fit while maintaining aspect ratio
            
            Future enhancement ideas:
            - Dynamic height adjustment based on instruction content length 
            - Responsive aspect ratios (3:4, 9:16) to reduce whitespace based on the instruction content length
            - Dynamically calculate instruction box size, that is number of lines etc, 
              and use it to optimize component dimensions and aspect ratios.
          */}
          <div className="max-h-[40vh] overflow-hidden mt-2">
            <div className="w-full h-full flex items-center justify-center">
              { renderEitherVideoOrImage(defn)}
            </div>
          </div>

          {/* Instructions: natural size until max-h-[30vh], content clipped if exceeds */}
          {/* The repsonsive is very static it just depends on the height of screen, we could
          defintely use content length etc as well to make it more responsive, but requires work. For example,
          a 1 liner instructions looks crappy compared to a 4 liner instruction */}
          { defn.instructions && defn.instructions.length > 0 ? 
            <div className="max-h-[30vh] overflow-auto mt-2 flex justify-center">
              <Markdown>
                {defn.instructions}
              </Markdown>
            </div>
          : null }

          <div className="flex-1 p-2 flex flex-col items-center overflow-hidden">
            {/* 
                Ideally we use our InBrowserCaptureButtons here but that never quite worked right on Android. We used
                to do this:

                    {getOSName() !== "Android" ? (
                      <InBrowserCaptureButtons onVideoSelected={() => {}} />
                    ) : (
                      <NativeCameraCaptureButtons onVideoSelected={() => {}} />
                    )}

                but then Apple released a new iOS release that added the tab bar to Safari when in portrait mode which
                broke things - specifically the tabs take up a bunch of room but also `vh` seemed to report the
                portrait mode viewport or something? In any event, the end result was that the part of the screen that
                showed the video capture was waaaay too tall so you could only see small portion of what you were
                filming and the record button was off the screen and you had to scroll to get to it. For now
                we just always show the native camera.
            */}
              <NativeCameraCaptureButtons onVideoSelected={() => {}} />

              <div className="flex flex-col text-[clamp(0.75rem,1.5vh,1rem)] text-center space-y-2">
                <Markdown popupContent={renderPopupContent(defn.tos)}>
                    {`Click here for [terms of service](popup).`}
                  </Markdown>
                <MobileCameraHelpLink />
                
                <NeedHelpContactSupport />
              </div>
          </div>
        </div>
      </div>
    )}
  />  
    )
};
